import type { Transition, Variants } from 'framer-motion';

/**
 * Shared motion vocabulary for the Leads page.
 *
 * Mirrors the "editorial" easing used elsewhere in the app (slow-out, fast-in —
 * reads as premium rather than bouncy). Kept local to `features/leads` so this
 * feature doesn't reach into `features/marketing`.
 *
 * Reduced motion: the page wraps its tree in `<MotionConfig reducedMotion="user">`,
 * which makes every `motion.*` element below skip transform/layout animation
 * automatically when the OS "reduce motion" preference is on. That covers all
 * variants in this file. Imperative `animate()` calls (see `CountUp.tsx`) are
 * NOT covered by MotionConfig and check `useReducedMotion()` themselves.
 */
export const EASE: Transition['ease'] = [0.22, 1, 0.36, 1];

/** Wrap a list (tbody / grid) to stagger its children's entrance. */
export const LIST_CONTAINER: Variants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.045, delayChildren: 0.02 },
  },
};

/** Row / card entrance — small rise + fade, quick enough to stay snappy in a dense list. */
export const LIST_ITEM: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: EASE },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.15, ease: EASE },
  },
};

/** Gentle scale-pop, used for badge value changes (status/priority transitions). */
export const BADGE_POP: Transition = { duration: 0.18, ease: EASE };
